import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { Address } from '@electricjs/arc';

type AddressValidationResponseDto = {
  result?: {
    verdict?: {
      addressComplete?: boolean;
    };
    address?: {
      missingComponentTypes?: string[];
    };
  };
};

export type ValidateAddressResponse = {
  isMissingSubpremise: boolean;
};

type GooglePostalAddress = {
  regionCode: string;
  locality: string;
  administrativeArea: string;
  postalCode: string;
  addressLines: string[];
};

/**
 * Transforms our Address type to Google's PostalAddress format
 */
const transformToGooglePostalAddress = (
  address: Address
): GooglePostalAddress => {
  const addressLines = [address.streetAddress1];
  if (address.streetAddress2) {
    addressLines.push(address.streetAddress2);
  }

  return {
    regionCode: address.country,
    locality: address.city,
    administrativeArea: address.state,
    postalCode: address.zip,
    addressLines,
  };
};

const baseQuery = fetchBaseQuery({
  baseUrl: 'https://addressvalidation.googleapis.com/v1',
});

export const googleApiSlice = createApi({
  reducerPath: 'googleApiSlice',
  baseQuery,
  endpoints: builder => ({
    validateAddress: builder.mutation<ValidateAddressResponse, Address>({
      query: address => ({
        url: `:validateAddress?key=${window._env_?.VITE_GOOGLE_MAPS_PUB_API_KEY}`,
        method: 'POST',
        body: {
          address: transformToGooglePostalAddress(address),
        },
      }),
      transformResponse: (response: AddressValidationResponseDto) => {
        const addressComplete = response.result?.verdict?.addressComplete;
        const missingComponentTypes =
          response.result?.address?.missingComponentTypes || [];

        const isMissingSubpremise =
          !addressComplete && missingComponentTypes.includes('subpremise');

        return { isMissingSubpremise };
      },
    }),
  }),
});

export const { useValidateAddressMutation } = googleApiSlice;
